import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Loader2, Copy, Check, Mail, User, Zap, FileWarning, Search, PencilLine } from 'lucide-react';
import { UnidadeConsumidora } from '../../types';
import { useToast } from '../../src/contexts/ToastContext';
import { supabase } from '../../src/lib/supabase';
import { logUsageEvent } from '../../src/lib/logger';
import { useAuth } from '../../src/contexts/AuthContext';
import UCSearchPicker, { composeEnderecoUC } from './UCSearchPicker';
import SignatariosSelector, { signatariosVazio } from '../../src/components/SignatariosSelector';
import { SignatariosEscolhidos } from '../../src/lib/termoSignatarios';
import { RegeracaoDocumento, doPayload } from './regeracaoDocumento';
import ConfirmModal from '../../src/components/ConfirmModal';
import { useFechamentoSeguro } from '../../src/hooks/useFechamentoSeguro';
import { useCompletarDadosUc } from '../../src/hooks/useCompletarDadosUc';
import { montarDadosDocumentoUc, seVazio, type DadosDocumentoUc } from '../../src/lib/dadosDocumentoUc';

interface ComunicadoAdvertenciaModalProps {
    isOpen: boolean;
    onClose: () => void;
    unidade?: UnidadeConsumidora | null;
    /** Preenche o formulário com a emissão anterior, quando veio de "Corrigir e gerar novamente". */
    regeracao?: RegeracaoDocumento | null;
}

const inputCls = "w-full border border-gray-200 dark:border-slate-700 rounded-xl px-4 py-2.5 text-sm focus:border-primary outline-none bg-white dark:bg-slate-900 dark:text-slate-100";
const labelCls = "block text-xs font-bold text-gray-500 dark:text-slate-400 mb-1 uppercase tracking-wider";
const sectionTitleCls = "text-sm font-bold text-gray-800 dark:text-slate-200 border-b border-gray-100 dark:border-slate-700 pb-2 flex items-center gap-2";

const ComunicadoAdvertenciaModal: React.FC<ComunicadoAdvertenciaModalProps> = ({ isOpen, onClose, unidade, regeracao }) => {
    const { addToast } = useToast();
    const { user } = useAuth();
    const [loading, setLoading] = useState(false);
    const [successData, setSuccessData] = useState<{ link: string; numero: string } | null>(null);
    const [copied, setCopied] = useState(false);
    // Vira true no primeiro toque do operador. O preenchimento automatico pela UC
    // nao conta: nao ha trabalho a perder ainda.
    const [rascunho, setRascunho] = useState(false);

    // Modo de preenchimento
    const [modo, setModo] = useState<'buscar' | 'manual'>('buscar');
    const [ucsSelecionadas, setUcsSelecionadas] = useState<UnidadeConsumidora[]>([]);

    // Cliente
    const [nome, setNome] = useState('');
    const [cpfCnpj, setCpfCnpj] = useState('');
    const [tipoCliente, setTipoCliente] = useState<'PF' | 'PJ'>('PF');

    const handleCpfCnpjChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        let val = e.target.value.replace(/\D/g, '');
        if (tipoCliente === 'PF') {
            if (val.length > 11) val = val.slice(0, 11);
            val = val.replace(/(\d{3})(\d)/, '$1.$2');
            val = val.replace(/(\d{3})(\d)/, '$1.$2');
            val = val.replace(/(\d{3})(\d{1,2})$/, '$1-$2');
        } else {
            if (val.length > 14) val = val.slice(0, 14);
            val = val.replace(/^(\d{2})(\d)/, '$1.$2');
            val = val.replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3');
            val = val.replace(/\.(\d{3})(\d)/, '.$1/$2');
            val = val.replace(/(\d{4})(\d)/, '$1-$2');
        }
        setCpfCnpj(val);
    };

    // Contato
    const [email, setEmail] = useState('');
    const [whatsapp, setWhatsapp] = useState('');

    // UC notificada
    const [numeroUc, setNumeroUc] = useState('');
    const [enderecoUc, setEnderecoUc] = useState('');
    const [ucId, setUcId] = useState<number | null>(null);

    // Data de emissão
    const [dataEmissao, setDataEmissao] = useState(new Date().toISOString().split('T')[0]);

    // Observações internas (não vão ao documento)
    const [obs, setObs] = useState('');

    // Representante legal que assina o comunicado (default: Config → Assinaturas do Termo)
    const [signatarios, setSignatarios] = useState<SignatariosEscolhidos>(signatariosVazio);

    const preencherDoCliente = (uc: UnidadeConsumidora) => {
        // Nome e CPF/CNPJ do mesmo cliente — ver montarDadosDocumentoUc.
        const d = montarDadosDocumentoUc(uc);
        setNome(d.nome);
        setCpfCnpj(d.cpfCnpj);
        setTipoCliente(d.tipoCliente);
        setEmail(d.email);
        setWhatsapp(d.whatsapp);
    };

    // O que o cadastro da UC não tem vem da negociação e da última fatura,
    // e só entra em campo vazio.
    const { completar, esquecer } = useCompletarDadosUc();
    const aplicarComplemento = (uc: UnidadeConsumidora, d: DadosDocumentoUc) => {
        setNome(seVazio(d.nome));
        // A UC não tinha CPF/CNPJ e a negociação tem: o tipo acompanha o documento.
        if (!montarDadosDocumentoUc(uc).cpfCnpj && d.cpfCnpj) {
            setCpfCnpj(seVazio(d.cpfCnpj));
            setTipoCliente(d.tipoCliente);
        }
        setEmail(seVazio(d.email));
        setWhatsapp(seVazio(d.whatsapp));
        setEnderecoUc(seVazio(d.enderecoTexto, composeEnderecoUC(uc)));
    };

    useEffect(() => {
        if (isOpen) {
            setSuccessData(null);
            setRascunho(false);
            setObs('');
            setDataEmissao(new Date().toISOString().split('T')[0]);
            setSignatarios(signatariosVazio);

            if (unidade) {
                setModo('buscar');
                setUcsSelecionadas([unidade]);
                preencherDoCliente(unidade);
                setUcId(unidade.id ?? null);
                setNumeroUc(unidade.numero_uc || '');
                setEnderecoUc(composeEnderecoUC(unidade));
                completar(unidade, d => aplicarComplemento(unidade, d));
            } else {
                esquecer();
                setModo('buscar');
                setUcsSelecionadas([]);
                setNome('');
                setCpfCnpj('');
                setTipoCliente('PF');
                setEmail('');
                setWhatsapp('');
                setUcId(null);
                setNumeroUc('');
                setEnderecoUc('');
            }

            // Regeração ("Corrigir e gerar novamente"): o que foi digitado na emissão
            // cancelada vale mais que o preenchimento automático pela UC — é justamente
            // o que o operador quer revisar. Data de emissão NÃO volta: o documento novo é de hoje.
            const p = regeracao?.payload;
            if (p) {
                const nomeAnterior = doPayload(p, 'cliente_nome');
                const docAnterior = doPayload(p, 'cpf_cnpj');
                if (nomeAnterior) setNome(nomeAnterior);
                if (docAnterior) {
                    setCpfCnpj(docAnterior);
                    setTipoCliente(docAnterior.replace(/\D/g, '').length === 14 ? 'PJ' : 'PF');
                }
                if (p.tipo_cliente === 'PF' || p.tipo_cliente === 'PJ') setTipoCliente(p.tipo_cliente);
                if (doPayload(p, 'email')) setEmail(doPayload(p, 'email'));
                if (doPayload(p, 'whatsapp')) setWhatsapp(doPayload(p, 'whatsapp'));
                if (doPayload(p, 'numero_uc')) setNumeroUc(doPayload(p, 'numero_uc'));
                if (doPayload(p, 'endereco_uc')) setEnderecoUc(doPayload(p, 'endereco_uc'));
                setObs(doPayload(p, 'obs'));
            }
        }
    }, [isOpen, unidade, regeracao]);

    const handleClose = () => {
        setSuccessData(null);
        onClose();
    };

    // Depois de gerado nao ha mais o que perder — o sucesso fecha sem perguntar.
    const { propsDoFundo, pedirParaFechar, confirmandoDescarte, descartarEFechar, continuarPreenchendo } =
        useFechamentoSeguro({ isOpen, temRascunho: rascunho && !successData, onFechar: handleClose });

    if (!isOpen) return null;

    const handleAddUcSelecionada = (uc: UnidadeConsumidora) => {
        setRascunho(true);
        setUcsSelecionadas([uc]);
        setUcId(uc.id ?? null);
        setNumeroUc(uc.numero_uc || '');
        setEnderecoUc(composeEnderecoUC(uc));
        preencherDoCliente(uc);
        completar(uc, d => aplicarComplemento(uc, d));
    };

    const handleRemoveUcSelecionada = (uc: UnidadeConsumidora) => {
        setRascunho(true);
        esquecer();
        setUcsSelecionadas(prev => prev.filter(s => s.id !== uc.id));
        setUcId(null);
        setNumeroUc('');
        setEnderecoUc('');
    };

    const handleGenerateComunicado = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!nome || !cpfCnpj || !email || !whatsapp) {
            addToast('Preencha os campos obrigatórios (Nome, CPF/CNPJ, E-mail e WhatsApp).', 'warning');
            return;
        }
        if (!numeroUc.trim()) {
            addToast('Informe a Unidade Consumidora a ser notificada.', 'warning');
            return;
        }
        if (!signatarios.procurador) {
            addToast('Selecione o representante legal que assinará o comunicado.', 'warning');
            return;
        }

        setLoading(true);

        try {
            const { data: settings } = await supabase
                .from('system_settings')
                .select('value')
                .eq('key', 'comunicado_advertencia_webhook_url')
                .single();

            const webhookUrl = settings?.value || '';

            if (!webhookUrl) {
                addToast('URL do Webhook de Comunicado de Advertência não configurada em Config > APIs.', 'error');
                setLoading(false);
                return;
            }

            const payload = {
                id: ucId,
                uc_ids: ucId ? [ucId] : [],
                cliente_nome: nome,
                cpf_cnpj: cpfCnpj,
                tipo_cliente: tipoCliente,
                email: email,
                whatsapp: whatsapp,
                numero_uc: numeroUc,
                endereco_uc: enderecoUc,
                ucs: [{ numero: numeroUc, endereco: enderecoUc }],
                data_emissao: dataEmissao,
                obs: obs,
                // Único signatário do documento — o cliente apenas recebe o PDF assinado
                procurador: signatarios.procurador
            };

            const response = await fetch(webhookUrl, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            if (response.ok) {
                const responseText = await response.text();
                let n8nResponse: any = null;
                if (responseText) {
                    try {
                        n8nResponse = JSON.parse(responseText);
                    } catch {
                        console.warn('Resposta do webhook não é JSON:', responseText);
                    }
                }

                if (n8nResponse && n8nResponse.link_assinatura_cliente) {
                    setSuccessData({
                        link: n8nResponse.link_assinatura_cliente,
                        numero: n8nResponse.numero_termo || ''
                    });
                    logUsageEvent({ userId: user?.id, modulo: 'financeiro', funcionalidade: 'ucs', acao: 'gerar_comunicado_advertencia', origem: 'acao' });

                    try {
                        await supabase.from('comunicado_advertencia_logs').insert({
                            uc_id: ucId,
                            uc_numero: numeroUc,
                            cliente_nome: nome,
                            // Link de acesso ao documento no Autentique (onde o representante legal assina).
                            // O PDF assinado (.../assinado.pdf) só existe DEPOIS que todos assinam — é montado
                            // sob demanda a partir de id_autentique (ver linkPdfAssinado em TermCancelHistoryModal.tsx),
                            // nunca gravado aqui.
                            url_pdf: n8nResponse.url_pdf || n8nResponse.link_assinatura_cliente,
                            numero_termo: n8nResponse.numero_termo || null,
                            user_id: user?.id || null,
                            tipo_cliente: tipoCliente,
                            // Contato do cliente — usado pelo fluxo "Autentique assinado → WhatsApp"
                            whatsapp_cliente: whatsapp,
                            email_cliente: email,
                            id_autentique: n8nResponse.id_autentique || null,
                            // Snapshot do formulário: o Autentique não deixa editar um documento
                            // criado, então "corrigir" é cancelar + emitir de novo — e é daqui
                            // que o modal renasce preenchido.
                            payload_geracao: payload,
                            // Quando esta emissão é a correção de uma anterior, guarda o elo.
                            substitui_log_id: regeracao?.substituiLogId ?? null
                        });
                    } catch (logError) {
                        console.error('Error logging advertência:', logError);
                    }

                    addToast('Comunicado de advertência gerado com sucesso!', 'success');
                } else {
                    throw new Error('Retorno inválido do webhook — Link não encontrado na resposta.');
                }
            } else {
                throw new Error(`Erro ${response.status}: ${response.statusText}`);
            }

        } catch (error: any) {
            console.error('Erro ao gerar comunicado de advertência:', error);
            addToast('Erro ao gerar comunicado: ' + (error.message || 'Erro desconhecido'), 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleCopyLink = () => {
        if (successData?.link) {
            navigator.clipboard.writeText(successData.link);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        }
    };

    return createPortal(
        <>
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 animate-fade-in" {...propsDoFundo}>
            <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-3xl overflow-hidden flex flex-col max-h-[90vh]" onClick={e => e.stopPropagation()}>

                {/* Header */}
                <div className="flex items-center justify-between p-4 md:p-6 border-b border-gray-100 dark:border-slate-700/50 bg-gray-50/50 dark:bg-slate-800/50">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-amber-50 dark:bg-amber-900/20 text-amber-600 dark:text-amber-500 rounded-lg border border-amber-100 dark:border-amber-800/50">
                            <FileWarning size={20} />
                        </div>
                        <div>
                            <h2 className="text-lg font-bold text-gray-800 dark:text-slate-100 font-poppins">Gerar Comunicado de Advertência</h2>
                            <p className="text-sm text-gray-500 dark:text-slate-400">Notificação extrajudicial por violação de exclusividade de compensação.</p>
                        </div>
                    </div>
                    <button
                        onClick={pedirParaFechar}
                        className="p-2 text-gray-400 dark:text-slate-500 hover:text-gray-600 dark:hover:text-slate-300 hover:bg-gray-100 dark:hover:bg-slate-800 rounded-lg transition-colors"
                    >
                        <X size={20} />
                    </button>
                </div>

                {/* Body */}
                <div className="flex-1 overflow-y-auto p-4 md:p-6">
                    {successData ? (
                        <div className="flex flex-col items-center justify-center text-center space-y-4 py-6">
                            <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 text-green-500 rounded-full flex items-center justify-center mb-2">
                                <Check size={32} />
                            </div>
                            <h3 className="text-xl font-bold text-gray-900 dark:text-slate-100">Comunicado Gerado com Sucesso!</h3>
                            <p className="text-sm text-gray-500 dark:text-slate-400 max-w-sm">
                                O documento aguarda a assinatura do representante legal no Autentique.
                                Assim que ele assinar, o cliente recebe o PDF assinado por WhatsApp automaticamente.
                            </p>
                            <p className="text-xs text-gray-400 dark:text-slate-500 max-w-sm">
                                O link abaixo é o de <strong>assinatura do representante</strong> — use-o para cobrar a assinatura interna.
                            </p>
                            {successData.numero && (
                                <p className="text-xs text-gray-400 dark:text-slate-500">
                                    N° do documento: <span className="font-mono font-semibold">{successData.numero}</span>
                                </p>
                            )}

                            <div className="w-full bg-gray-50 dark:bg-slate-800 p-4 rounded-xl border border-gray-200 dark:border-slate-700 mt-4 flex items-center justify-between">
                                <span className="text-sm text-gray-600 dark:text-slate-300 truncate mr-4">{successData.link}</span>
                                <button
                                    onClick={handleCopyLink}
                                    className="flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-600 rounded-lg text-sm text-gray-700 dark:text-slate-200 hover:bg-gray-50 dark:hover:bg-slate-800 transition-colors shadow-sm"
                                >
                                    {copied ? <Check size={14} className="text-green-500" /> : <Copy size={14} />}
                                    {copied ? 'Copiado!' : 'Copiar'}
                                </button>
                            </div>
                        </div>
                    ) : (
                        <form id="advertencia-form" onSubmit={handleGenerateComunicado} onChange={() => setRascunho(true)} className="space-y-6">

                            {/* Aviso sobre o documento */}
                            <div className="bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-800/50 rounded-xl p-4 text-sm text-amber-800 dark:text-amber-300">
                                Notifica o associado de que a UC está <strong>recebendo créditos de outra empresa/entidade</strong> (violação da cláusula de exclusividade), com prazo de <strong>15 dias corridos</strong> para regularização.
                            </div>

                            {/* Modo de preenchimento */}
                            <div className="space-y-3">
                                <h3 className={sectionTitleCls}>
                                    <Search size={16} className="text-primary" /> Origem dos Dados
                                </h3>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                    <button
                                        type="button"
                                        onClick={() => setModo('buscar')}
                                        className={`flex items-start gap-3 p-3.5 rounded-xl border-2 text-left transition-all ${
                                            modo === 'buscar'
                                                ? 'border-primary bg-primary/5 dark:bg-primary/10'
                                                : 'border-gray-200 dark:border-slate-700 hover:border-gray-300 dark:hover:border-slate-600'
                                        }`}
                                    >
                                        <Search size={16} className={`mt-0.5 flex-shrink-0 ${modo === 'buscar' ? 'text-primary' : 'text-gray-400'}`} />
                                        <div>
                                            <p className="text-sm font-semibold text-gray-800 dark:text-slate-100">Buscar UC Existente</p>
                                            <p className="text-xs text-gray-500 dark:text-slate-400 mt-0.5">Preencher a partir de uma UC cadastrada.</p>
                                        </div>
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setModo('manual')}
                                        className={`flex items-start gap-3 p-3.5 rounded-xl border-2 text-left transition-all ${
                                            modo === 'manual'
                                                ? 'border-primary bg-primary/5 dark:bg-primary/10'
                                                : 'border-gray-200 dark:border-slate-700 hover:border-gray-300 dark:hover:border-slate-600'
                                        }`}
                                    >
                                        <PencilLine size={16} className={`mt-0.5 flex-shrink-0 ${modo === 'manual' ? 'text-primary' : 'text-gray-400'}`} />
                                        <div>
                                            <p className="text-sm font-semibold text-gray-800 dark:text-slate-100">Preencher Manualmente</p>
                                            <p className="text-xs text-gray-500 dark:text-slate-400 mt-0.5">Digitar todas as informações do comunicado.</p>
                                        </div>
                                    </button>
                                </div>
                                {modo === 'buscar' && (
                                    <UCSearchPicker
                                        selecionadas={ucsSelecionadas}
                                        onAdd={handleAddUcSelecionada}
                                        onRemove={handleRemoveUcSelecionada}
                                        max={1}
                                        placeholder="Buscar UC por cliente, nº da UC ou CPF/CNPJ..."
                                    />
                                )}
                            </div>

                            {/* Cliente Section */}
                            <div className="space-y-4">
                                <h3 className={sectionTitleCls}>
                                    <User size={16} className="text-primary" /> Dados do Associado
                                </h3>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div className="md:col-span-2">
                                        <label className={labelCls}>Nome Completo / Razão Social *</label>
                                        <input type="text" required value={nome} onChange={e => setNome(e.target.value)} className={inputCls} />
                                    </div>
                                    <div>
                                        <label className={labelCls}>Tipo *</label>
                                        <select value={tipoCliente} onChange={e => {
                                            setTipoCliente(e.target.value as 'PF' | 'PJ');
                                            setCpfCnpj('');
                                        }} className={inputCls}>
                                            <option value="PF">Pessoa Física</option>
                                            <option value="PJ">Pessoa Jurídica</option>
                                        </select>
                                    </div>
                                    <div>
                                        <label className={labelCls}>CPF / CNPJ *</label>
                                        <input
                                            type="text"
                                            required
                                            value={cpfCnpj}
                                            onChange={handleCpfCnpjChange}
                                            className={inputCls}
                                            placeholder={tipoCliente === 'PF' ? '000.000.000-00' : '00.000.000/0000-00'}
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Contato Section */}
                            <div className="space-y-4">
                                <h3 className={sectionTitleCls}>
                                    <Mail size={16} className="text-primary" /> Contato do Notificado (recebe o documento)
                                </h3>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className={labelCls}>E-mail *</label>
                                        <input type="email" required value={email} onChange={e => setEmail(e.target.value)} className={inputCls} />
                                    </div>
                                    <div>
                                        <label className={labelCls}>WhatsApp *</label>
                                        <input type="text" required value={whatsapp} onChange={e => setWhatsapp(e.target.value)} className={inputCls} />
                                    </div>
                                </div>
                            </div>

                            {/* UC Notificada */}
                            <div className="space-y-4">
                                <h3 className={sectionTitleCls}>
                                    <Zap size={16} className="text-primary" /> Unidade Consumidora Notificada
                                </h3>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className={labelCls}>Número da UC *</label>
                                        <input type="text" value={numeroUc} onChange={e => setNumeroUc(e.target.value)} className={inputCls} />
                                    </div>
                                    <div>
                                        <label className={labelCls}>Data de Emissão *</label>
                                        <input
                                            type="date"
                                            required
                                            value={dataEmissao}
                                            onChange={e => setDataEmissao(e.target.value)}
                                            className={inputCls}
                                        />
                                    </div>
                                    <div className="md:col-span-2">
                                        <label className={labelCls}>Endereço da UC</label>
                                        <input type="text" value={enderecoUc} onChange={e => setEnderecoUc(e.target.value)} placeholder="Rua, número, bairro, cidade/UF" className={inputCls} />
                                    </div>
                                </div>
                            </div>

                            {/* Signatário interno (único assinante do documento) */}
                            <SignatariosSelector
                                comProcurador
                                comTestemunhas={false}
                                value={signatarios}
                                onChange={setSignatarios}
                            />

                            {/* Observações internas */}
                            <div>
                                <label className={labelCls}>Observações Internas (não constam no documento)</label>
                                <textarea
                                    value={obs}
                                    onChange={e => setObs(e.target.value)}
                                    rows={3}
                                    placeholder="Ex: rateio concorrente identificado na Equatorial em 28/06..."
                                    className={`${inputCls} resize-y`}
                                />
                            </div>

                        </form>
                    )}
                </div>

                {/* Footer */}
                {!successData && (
                    <div className="p-4 border-t border-gray-100 dark:border-slate-700/50 bg-gray-50 dark:bg-slate-800/80 flex justify-end gap-3">
                        <button
                            type="button"
                            onClick={pedirParaFechar}
                            disabled={loading}
                            className="px-4 py-2 bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-700 text-gray-700 dark:text-slate-200 rounded-xl text-sm font-semibold hover:bg-gray-50 dark:hover:bg-slate-800 transition-colors shadow-sm disabled:opacity-50"
                        >
                            Cancelar
                        </button>
                        <button
                            type="submit"
                            form="advertencia-form"
                            disabled={loading}
                            className="px-6 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-xl text-sm font-bold transition-colors shadow-sm disabled:opacity-70 flex items-center justify-center min-w-[160px]"
                        >
                            {loading ? <Loader2 size={16} className="animate-spin" /> : 'Gerar Advertência'}
                        </button>
                    </div>
                )}
            </div>
        </div>

        <ConfirmModal
            isOpen={confirmandoDescarte}
            onClose={continuarPreenchendo}
            onConfirm={descartarEFechar}
            title="Descartar o comunicado?"
            message="Você já preencheu dados deste comunicado de advertência. Se fechar agora, tudo o que foi digitado se perde."
            confirmLabel="Descartar"
            cancelLabel="Continuar preenchendo"
            variant="warning"
        />
        </>,
        document.body
    );
};

export default ComunicadoAdvertenciaModal;
